import type { AnchorHTMLAttributes } from "react";
import { Link, type LinkProps } from "react-router";
import type { Decision } from "../../api/access/decisions";
import { buttonClassName, type ButtonSize, type ButtonVariant } from "../styles/buttonStyles";
import { classNames } from "../styles/classNames";
import AppButton from "./AppButton";

interface ButtonLook {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  /**
   * The served decision following the link needs (SOT-02, accounts.md GS-8). Refused, it is
   * no link: it shows as `AppButton` shows a refused action, with why.
   */
  requires?: Decision;
}

/** An app route (`to`), or an address outside the router, such as a download (`href`). */
type ButtonLinkProps = ButtonLook & (Omit<LinkProps, "className"> | (Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className"> & { href: string }));

/**
 * A link that looks like a button (F48): a call to action that goes to another page, such as
 * "Continue setup", or a download. It stays a link, so it opens in a new tab like one; an
 * action that does something on the page is an `AppButton`, and a plain text link only goes
 * to a page or document, never several in a row.
 */
export default function ButtonLink({ variant = "secondary", size = "sm", className, requires, ...props }: ButtonLinkProps) {
  if (requires !== undefined && requires.refusal !== null) {
    return <AppButton requires={requires} variant={variant} size={size} className={className}>{props.children}</AppButton>;
  }
  const look = classNames(buttonClassName(variant, size), className);
  return "to" in props ? <Link {...props} className={look} /> : <a {...props} className={look} />;
}
